import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Alert, Spin } from "antd";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useGlobalHandler from "../../hooks/useGlobalHandler";
import { logIn } from "../../hooks/useSignup";
import { useTranslation } from "react-i18next";

const LoginForm = () => {
  const { t } = useTranslation("preAuth");

  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("invalid email"))
      .required(t("email is required")),
    password: Yup.string()
      .required(t("no password provided"))
      .min(8, "Password is too short - should be 8 chars minimum.")
      .matches(/[a-zA-Z]/, "Password can only contain Latin letters."),
  });
  const {
    isLoading,
    setIsLoading,
    isError,
    setIsError,
    error,
    setError,
    success,
  } = useGlobalHandler();
  const navigate = useNavigate();
  const [showPassword, setshowPassword] = useState(false);

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
      }}
      validationSchema={loginSchema}
      onSubmit={async (values) => {
        try {
          setIsError(false);
          setError("");
          setIsLoading(true);
          await logIn(values.email, values.password);
          navigate("/");
        } catch (error) {
          setIsError(true);
          switch (error.code) {
            case "auth/invalid-credential":
              setError("Invalid Credential");
              break;
            case "auth/network-request-failed":
              setError("Network Error, Please Try Again");
              break;
            case "auth/email-already-in-use":
              setError(
                "Email already used, if you already have an account try login"
              );
              break;
            default:
              setError(error.code);
              break;
          }
          setIsLoading(false);
        }
      }}
    >
      {({ errors, touched }) => (
        <div className="bg-white py-10 px-8 rounded-lg md:w-1/3 min-w-[94%] md:min-w-max">
          <Form className="flex flex-col gap-4  ">
            <div className="flex flex-col gap-1">
              <h1 className="LoginTtitle">{t("login")}</h1>
              <p className="font-light text-[#404040] max-w-80">
                {t(
                  "enter your email address and password to access your panel"
                )}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {" "}
              <label className="text-primaryColor">{t("email")} </label>
              <Field
                className="border-2 rounded-md p-1"
                name="email"
                type="email"
                placeholder={t("email")}
              />
              <span className="flex flex-col gap-2 relative  ">
                <label className="text-primaryColor ">{t("password")} </label>
                <Field
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("password")}
                  className="border-2 rounded-md p-1"
                />
                <EyeOutlined
                  className={`absolute top-[63%] right-2 ${
                    showPassword ? "" : "hidden"
                  }`}
                  onClick={() => setshowPassword(!showPassword)}
                />
                <EyeInvisibleOutlined
                  className={`absolute top-[63%] right-2 ${
                    !showPassword ? "" : "hidden"
                  }`}
                  onClick={() => setshowPassword(!showPassword)}
                />
              </span>
              {isError && (
                <Alert message={error} type="error" showIcon closable />
              )}
              {success && (
                <Alert message={success} type="success" showIcon closable />
              )}
              {errors.password && touched.password ? (
                <Alert message={errors.password} type="error" showIcon />
              ) : null}
              {errors.email && touched.email ? (
                <Alert message={errors.email} type="error" showIcon />
              ) : null}
              <span className="flex justify-between items-center">
                <label className="flex items-center gap-1 text-primaryColor">
                  <input
                    type="checkbox"
                    id="cbox1"
                    value="first_checkbox"
                    className="border-primaryColor h-4 w-4 checked:bg-primaryColor  "
                  />{" "}
                  {t("remember me")}
                </label>
                <Link
                  to={"/auth/forgotpassword"}
                  className="text-gray-500 underline underline-offset-1"
                >
                  {t("forgot Password")}
                </Link>
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <motion.button
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                className="primaryLoginButton"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? <Spin /> : t("login")}
              </motion.button>
            </div>
            <p className="self-center">
              {t("don’t have an account? create one")}{" "}
              <Link
                to="/auth/inscription"
                className="underline underline-offset-1 text-primaryColor"
              >
                {t("here")}
              </Link>
              .
            </p>
          </Form>
        </div>
      )}
    </Formik>
  );
};

export default LoginForm;
